import React from "react";
import { Clock3, CreditCard, FileText, ImagePlus, Pencil } from "lucide-react";

import { BORDER_SMART } from "./configHelpers";
import { Card } from "./ClientConfigShared";

const valueOrDash = (value) =>
  value === null || value === undefined || String(value).trim() === ""
    ? "—"
    : String(value);

const formatTime = (value) => {
  const time = String(value || "").trim();
  if (!time) return "—";

  const match = time.match(/^(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : time;
};

export default function OrganizationImageCard({
  organization,
  clientName,
  preview,
  loading,
  onEdit,
}) {
  const imageUrl = preview || organization?.brand_image;
  const organizationName =
    organization?.organizationName ||
    organization?.organization_name ||
    clientName ||
    "Organization";

  const paymentGateway =
    organization?.pament_gateway || organization?.payment_gateway || "";

  return (
    <Card
      title="Organization Details"
      icon={ImagePlus}
      rightAction={
        <button
          type="button"
          onClick={onEdit}
          disabled={loading}
          className="inline-flex h-7 items-center justify-center rounded-md bg-[#01C37D] px-2.5 text-[10px] font-bold text-white transition hover:bg-[#00ab6d] disabled:cursor-not-allowed disabled:opacity-60"
        >
          Edit
        </button>
      }
    >
      <div className="relative overflow-hidden rounded-lg border border-emerald-200 bg-gradient-to-r from-[#00c57c] to-[#03ac6e] px-2.5 py-2 shadow-sm">
        <div className="flex items-center gap-2.5">
          <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg border-2 border-white bg-white shadow-md">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="organization"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="grid h-full w-full place-items-center bg-gray-100 text-gray-400">
                <ImagePlus size={22} />
              </div>
            )}

            <button
              type="button"
              onClick={onEdit}
              disabled={loading}
              className="absolute bottom-0.5 right-0.5 rounded-full bg-white p-1 shadow-sm transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Pencil size={10} />
            </button>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="truncate text-[12px] font-black leading-none text-white drop-shadow">
                {organizationName}
              </div>

              {organization?.brand_image ? (
                <span className="shrink-0 rounded-full border border-white/20 bg-white/20 px-2 py-0.5 text-[9px] font-bold text-white backdrop-blur">
                  Synced
                </span>
              ) : null}
            </div>

            <p className="mt-1 truncate text-[10px] font-semibold leading-none text-white/90">
              Organization API settings
            </p>

            <div className="mt-1.5 truncate rounded-md border border-white/20 bg-white/15 px-2 py-1 text-[9px] font-medium text-white/90 backdrop-blur">
              Name, tax details, brand image, gateway and timings.
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-1.5">
        <InfoItem
          icon={FileText}
          label="GST"
          value={valueOrDash(organization?.gst)}
        />
        <InfoItem
          icon={FileText}
          label="PAN"
          value={valueOrDash(organization?.pan_no)}
        />
        <InfoItem
          icon={CreditCard}
          label="Gateway"
          value={valueOrDash(paymentGateway)}
        />
        <InfoItem
          icon={Clock3}
          label="Timing"
          value={`${formatTime(organization?.opening_time)} - ${formatTime(
            organization?.closing_time,
          )}`}
        />
      </div>

      {organization?.brand_image ? (
        <div
          className={`mt-1.5 rounded-md border ${BORDER_SMART} bg-gray-50 px-2 py-1 text-[9px] font-medium text-gray-500`}
        >
          Brand image URL is synced with organization details.
        </div>
      ) : null}
    </Card>
  );
}

function InfoItem({ icon: Icon, label, value }) {
  return (
    <div className="min-w-0 rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5">
      <div className="flex items-center gap-1 text-[8px] font-bold uppercase tracking-wide text-gray-400">
        <Icon size={9} />
        {label}
      </div>
      <div className="mt-0.5 truncate text-[9px] font-semibold text-gray-700">
        {value}
      </div>
    </div>
  );
}
